import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import {
    DatePicker as KendoDatePicker,
    DatePickerHandle,
    DateInput,
    ToggleButton,
    DatePickerChangeEvent,
    DateInputChangeEvent,
    DateInputProps,
    DatePickerOpenEvent,
    DatePickerCloseEvent,
} from '@progress/kendo-react-dateinputs';
import { InputSuffix } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import moment from 'moment';
import { DatePickerProps, DatePickerControllerProps } from '@components-clean/core/date-picker/date-picker.interface';
import svgStyles from '@components-clean/core/drag-drop-lists-core/style.module.scss';
import ValidationIconSuffix from 'views-clean/components/core/icons/validation-icon-suffix';

const TestInput = (iconSize: 'large' | 'small') =>
    function render({
        value,
        disabled,
        onChange,
        valid,
        validationMessage,
        placeholder,
        ...inputProps
    }: DateInputProps) {
        return (
            <>
                <DateInput
                    {...inputProps}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    placeholder={!disabled ? placeholder : null}
                />
                {!!value && !disabled && (
                    <InputSuffix
                        onClick={() => {
                            onChange && onChange({ value: null } as DateInputChangeEvent);
                        }}
                    >
                        <SvgIcon
                            icon={xIcon}
                            height={iconSize === 'small' ? '16px' : undefined}
                            style={{
                                paddingBlock: iconSize === 'small' ? 4 : undefined,
                                paddingInline: iconSize === 'small' ? 0 : undefined,
                            }}
                            size="large"
                            className={svgStyles.svgClear}
                        />
                    </InputSuffix>
                )}
                {valid === false && <ValidationIconSuffix validationMessage={validationMessage} padding="small" />}
            </>
        );
    };

const ReadonlyTestInput = ({ ...inputProps }: DateInputProps) => {
    return <DateInput {...inputProps} readonly onChange={() => null} />;
};

const DateFormat = 'dd-MMM-y';
const DisabledDateFormat = 'dd MMM y';

const DatePicker = ({
    'data-testid': testId,
    onOpen,
    onClose,
    className,
    inline,
    readonly,
    onChange,
    value,
    disabled,
    label,
    min,
    max,
    required,
    placeholder,
    columnSearch = false,
    ...props
}: DatePickerProps) => {
    const ref = useRef<DatePickerHandle>(null);
    const hasMinOrMax = min !== undefined || max !== undefined;
    const show = readonly ? false : props.show;

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const realOnChange = (event: DatePickerChangeEvent) => {
        const minToUse = min === 'future' ? moment().toDate() : min;
        const maxToUse = max === 'past' ? moment().toDate() : max;
        const inputDate = moment(event.target.value, 'DD-MMM-YYYY', true);
        // Based on UTC the computer's default date format & start date
        const isYearValid = inputDate.isValid() && inputDate.year() >= 1970;

        if (
            hasMinOrMax &&
            onChange &&
            event.target.value !== null &&
            event.target.value !== undefined &&
            event.target.value !== value &&
            isYearValid
        ) {
            if (minToUse && event.target.value < minToUse) {
                onChange({ ...event, target: { value: minToUse } } as DatePickerChangeEvent);
                return;
            }
            if (maxToUse && event.target.value > maxToUse) {
                onChange({ ...event, target: { value: maxToUse } } as DatePickerChangeEvent);
                return;
            }
        }
        if (event.target.value !== value && onChange) {
            onChange(event);
        }
    };

    // Add an open handler so that we can add a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onOpenHandler = (event: DatePickerOpenEvent) => {
        setIsOpen(true);

        if (onOpen) {
            onOpen(event);
        }
    };

    // Add a close handler so that we can remove a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onCloseHandler = (event: DatePickerCloseEvent) => {
        setIsOpen(false);

        if (onClose) {
            onClose(event);
        }
    };

    const classes = cn(className, { 'opra-dateinput-open': isOpen, hint: !value });

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const activeInput = useMemo(() => TestInput(columnSearch ? 'small' : 'large'), [disabled]);
    return (
        <div className="k-w-full">
            <Label
                editorDisabled={disabled}
                editorRef={ref}
                className={cn(inline ? ['!k-pb-0', 'k-white-space-nowrap'] : null)}
            >
                {label}
            </Label>
            <KendoDatePicker
                {...props}
                className={classes}
                onOpen={onOpenHandler}
                onClose={onCloseHandler}
                ref={ref}
                format={disabled && !value ? DisabledDateFormat : DateFormat}
                formatPlaceholder={
                    !disabled ? { day: 'DD', month: 'Mmm', year: 'YYYY' } : { day: ' ', month: ' ', year: ' ' }
                }
                min={min === 'future' ? moment().toDate() : min}
                max={max === 'past' ? moment().toDate() : max}
                disabled={disabled}
                onChange={realOnChange}
                value={value}
                dateInput={readonly ? ReadonlyTestInput : activeInput}
                toggleButton={(tbProps) => (
                    <ToggleButton
                        {...tbProps}
                        className={cn([tbProps.className, 'date-toggle-button'])}
                        style={columnSearch ? { width: 20 } : undefined}
                    />
                )}
                show={show}
            />
        </div>
    );
};

const DatePickerController = <TControl extends FieldValues>({
    control,
    name,
    disabled = false,
    'data-testid': dataTestId,
    label,
    min,
    max,
    required = false,
    readonly = false,
    fillMode = 'solid',
    placeholder,
    defaultValue,
    handleInputChange,
    ...props
}: DatePickerControllerProps<TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
            }}
            defaultValue={defaultValue as PathValue<TControl, Path<TControl>>}
            render={({ field: { onChange, value, name: fieldName }, fieldState: { error } }) => {
                return (
                    <DatePicker
                        name={fieldName}
                        label={label}
                        data-testid={dataTestId}
                        value={value as Date | null}
                        fillMode={fillMode}
                        onChange={(event) => {
                            onChange(event);
                            if (handleInputChange) handleInputChange(event);
                        }}
                        placeholder={placeholder}
                        valid={!error}
                        validationMessage={error?.message}
                        min={min}
                        max={max}
                        disabled={disabled}
                        readonly={readonly}
                        {...props}
                    />
                );
            }}
        />
    );
};

export { DatePicker, DatePickerController };
